import { FJMediaImportModelType } from '@src/constants/FJMediaImportConstant.js';

class FJMediaImportModel {
    constructor({
        id,
        name,
        url,
        thumbnail,
        modelType,
        type,
        mimeType,
        size,
        width,
        height,
        duration,
        folder,
        baseUrl,
    }) {
        this.id = id;
        this.name = name;
        this.url = url;
        this.thumbnail = thumbnail;
        this.modelType = modelType;
        this.type = type;

        this.mimeType = mimeType;
        this.size = size;
        this.width = width;
        this.height = height;
        this.duration = duration;
        this.folder = folder;
        this.baseUrl = baseUrl;
    }

    static mimeTypeToType(mimeType) {
        if (!mimeType) {
            return;
        }

        if (mimeType.includes('gif')) {
            return 'GIF';
        } else if (mimeType.includes('image/')) {
            return 'IMAGE';
        } else if (mimeType.includes('video/')) {
            return 'VIDEO';
        } else if (mimeType.includes('audio/')) {
            return 'AUDIO';
        }
    }

    static getProxyUrl(url) {
        const proxyUrl = 'https://app.flexclip.com/proxy/';
        return proxyUrl + url;
    }

    static fromGoogleDrive(json) {
        const id = json['id'];
        const name = json['name'];
        const thumbnail = json['thumbnailLink'];
        const url = `https://www.googleapis.com/drive/v3/files/${id}?alt=media`;
        const size = json['size'];
        const mimeType = json['mimeType'];
        let type = this.mimeTypeToType(mimeType);
        let modelType;
        if (mimeType.includes('folder')) {
            modelType = FJMediaImportModelType.folder;
        } else {
            modelType = FJMediaImportModelType.file;
        }
        const metaData = json['imageMediaMetadata'] ?? json['videoMediaMetadata'];
        const width = metaData?.width;
        const height = metaData?.height;
        const duration = (metaData?.durationMillis ?? 0) / 1000;
        return new FJMediaImportModel({
            id,
            name,
            url,
            thumbnail,
            modelType,
            type,
            mimeType,
            size,
            width,
            height,
            duration,
            folder: id,
        });
    }

    static fromOnedrive(json) {
        const id = json['id'];
        const name = json['name'];
        const thumbnail = json['thumbnails']?.[0]?.['medium']?.['url'];
        const size = json['size'];
        // oneDrive 的下载链接文档描述只有一个小时的有效时间，所以需要通过另外的请求拿到最新的链接
        const url = json['@microsoft.graph.downloadUrl'];

        let modelType = FJMediaImportModelType.file;
        if (json['folder']) {
            modelType = FJMediaImportModelType.folder;
        }

        let duration, width, height;
        if (json['image']) {
            width = json['image']['width'];
            height = json['image']['height'];
            duration = 0;
        } else if (json['video']) {
            width = json['video']['width'];
            height = json['video']['height'];
            duration = json['video']['duration'] / 1000;
        }

        let mimeType = null;
        if (json['file']) {
            mimeType = json['file']['mimeType'];
        }
        const type = this.mimeTypeToType(mimeType);

        return new FJMediaImportModel({
            id,
            name,
            thumbnail,
            size,
            modelType,
            mimeType,
            type,
            width,
            height,
            duration,
            folder: id,
            baseUrl: url,
        });
    }

    static fromGooglePhotos(json) {
        const id = json['id'];

        const info = json['mediaFile'];

        const name = info['filename'];
        const baseUrl = info['baseUrl'];
        const modelType = FJMediaImportModelType.file;

        const mimeType = info['mimeType'];
        const type = this.mimeTypeToType(mimeType);

        const thumbnail = `/v1/user/get-photo?url=${baseUrl}=w250-h140&content_type=${mimeType}`;

        const metaData = info['mediaFileMetadata'];
        const width = metaData?.['width'];
        const height = metaData?.['height'];

        // let url = baseUrl;
        // if (metaData?.['photo']) {
        //     url += '=d';
        // } else if (metaData?.['video']) {
        //     url += '=dv';
        // }

        return new FJMediaImportModel({
            id,
            name,
            thumbnail,
            // url,
            modelType,
            width,
            height,
            type,
            baseUrl,
            mimeType,
        });
    }

    static fromDropbox(json) {
        const id = json['id'];
        const name = json['name'];
        const size = json['size'];
        const tempThumbnail = json['thumbnail'];
        const thumbnail = tempThumbnail ? `data:Thumbnail/jpeg;base64,${tempThumbnail}` : null;
        let modelType;
        const tag = json['.tag'];
        if (tag === 'folder') {
            modelType = FJMediaImportModelType.folder;
        } else if (tag === 'file') {
            modelType = FJMediaImportModelType.file;
        }
        const folder = json['path_lower'];
        const type = this.fileNameToType(name);

        return new FJMediaImportModel({
            id,
            name,
            thumbnail,
            type,
            size,
            modelType,
            folder,
        });
    }

    //https://developers.facebook.com/docs/instagram-basic-display-api/reference/media
    static fromInstagram(json) {
        const id = json['id'];
        // const name = json['caption'] ?? '';
        const baseUrl = json['media_url'];
        const url = this.getProxyUrl(baseUrl);
        const thumbnail = json['thumbnail_url'] ?? baseUrl;
        const modelType = FJMediaImportModelType.file;
        const mediaType = json['media_type'];
        let type;
        if (mediaType === 'VIDEO') {
            type = 'VIDEO';
        } else {
            type = 'IMAGE';
        }
        return new FJMediaImportModel({ id, url, thumbnail, modelType, type, baseUrl });
    }

    static fromFacebook(json) {
        const id = json['id'];
        // const name = json['name'] ?? '';
        const images = json['images'] ?? [];
        const thumbnailData = images.reduce((pre, cur) => (cur.width < pre.width ? cur : pre));
        const thumbnail = thumbnailData?.['source'];
        const urlData = images.reduce((pre, cur) => (cur.width > pre.width ? cur : pre));
        const url = this.getProxyUrl(urlData?.['source']);
        const modelType = FJMediaImportModelType.file;

        const width = urlData?.['width'];
        const height = urlData?.['height'];
        const type = 'IMAGE';

        return new FJMediaImportModel({
            id,
            // name,
            url,
            thumbnail,
            modelType,
            width,
            height,
            type,
        });
    }
}

export default FJMediaImportModel;
